import React, { useRef, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { defaultCorners, moveCorner } from './cardGeometry';
import { flattenCard } from './cardImage';

const MAGNIFIER = 96;
const ZOOM = 3;
const KEY_STEP = 0.01;

// Full-screen crop step: drag the four corners onto the card, then it is cut out and
// straightened. "Use full photo" skips it.
const CardCropper = ({ image, title, onDone, onCancel }) => {
  const { t } = useTranslation();
  const [corners, setCorners] = useState(() => defaultCorners());
  const [drag, setDrag] = useState(null); // { index, width, height } while a corner is held
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const frameRef = useRef(null);

  const pointFromEvent = (e) => {
    const rect = frameRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
  };

  const startDrag = (index) => (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = frameRef.current.getBoundingClientRect();
    setDrag({ index, width: rect.width, height: rect.height });
  };

  const onMove = (index) => (e) => {
    if (drag?.index !== index) return;
    setCorners((c) => moveCorner(c, index, pointFromEvent(e)));
  };

  const endDrag = () => setDrag(null);

  const onKey = (index) => (e) => {
    const step = { ArrowLeft: [-KEY_STEP, 0], ArrowRight: [KEY_STEP, 0], ArrowUp: [0, -KEY_STEP], ArrowDown: [0, KEY_STEP] }[e.key];
    if (!step) return;
    e.preventDefault();
    setCorners((c) => moveCorner(c, index, { x: c[index].x + step[0], y: c[index].y + step[1] }));
  };

  const confirm = async () => {
    setBusy(true);
    setFailed(false);
    try {
      onDone(await flattenCard(image, corners));
    } catch (error) {
      console.error('Card crop failed:', error);
      setFailed(true);
      setBusy(false);
    }
  };

  const polygon = corners.map((c) => `${c.x},${c.y}`).join(' ');
  const active = drag ? corners[drag.index] : null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('businessCardScanner.close')}
          className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center hover:bg-white/25"
        >
          <CloseOutlined />
        </button>
        <span className="font-semibold">{title}</span>
        <span className="w-10" />
      </div>

      <div className="relative flex-1 min-h-0 flex items-center justify-center px-6">
        {drag && (
          <div
            aria-hidden="true"
            data-testid="crop-magnifier"
            className="absolute top-2 start-1/2 -translate-x-1/2 rounded-full border-2 border-white shadow-lg pointer-events-none z-10"
            style={{
              width: MAGNIFIER,
              height: MAGNIFIER,
              backgroundImage: `url(${image})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${drag.width * ZOOM}px ${drag.height * ZOOM}px`,
              backgroundPosition: `${MAGNIFIER / 2 - active.x * drag.width * ZOOM}px ${MAGNIFIER / 2 - active.y * drag.height * ZOOM}px`,
            }}
          >
            <span className="absolute top-1/2 start-1/2 w-2 h-2 -mt-1 -ms-1 rounded-full bg-blue-500 ring-2 ring-white" />
          </div>
        )}

        <div ref={frameRef} className="relative inline-block select-none" style={{ touchAction: 'none' }}>
          <img
            src={image}
            alt=""
            draggable={false}
            className="block max-w-full object-contain"
            style={{ maxHeight: 'calc(100vh - 220px)' }}
          />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <path d={`M0,0H1V1H0Z M${polygon.replace(/ /g, 'L')}Z`} fill="rgba(0,0,0,0.55)" fillRule="evenodd" />
            <polygon points={polygon} fill="none" stroke="#fff" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </svg>
          {corners.map((c, i) => (
            <button
              key={i}
              type="button"
              role="slider"
              aria-label={t('businessCardScanner.cropCorner', { n: i + 1 })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(c.x * 100)}
              aria-valuetext={`${Math.round(c.x * 100)}%, ${Math.round(c.y * 100)}%`}
              onPointerDown={startDrag(i)}
              onPointerMove={onMove(i)}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={onKey(i)}
              className="absolute w-11 h-11 -ms-[22px] -mt-[22px] flex items-center justify-center rounded-full focus:outline-none group"
              style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, touchAction: 'none' }}
            >
              <span className="w-5 h-5 rounded-full bg-white border-2 border-blue-600 shadow group-focus-visible:ring-4 group-focus-visible:ring-blue-400" />
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-3 pb-5 flex flex-col items-center gap-3 text-white">
        <p className={`text-sm ${failed ? 'text-red-300' : 'text-white/70'}`} aria-live="polite">
          {failed ? t('businessCardScanner.cropFailed') : t('businessCardScanner.cropHint')}
        </p>
        <div className="w-full max-w-md flex items-center gap-4">
          <button
            type="button"
            onClick={() => onDone(image)}
            disabled={busy}
            className="text-sm text-white/80 underline underline-offset-4 disabled:opacity-50"
          >
            {t('businessCardScanner.useFullPhoto')}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? t('businessCardScanner.cropProcessing') : t('businessCardScanner.cropDone')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardCropper;
